import { styled, SxProps, Theme } from '@mui/material';
import { ComponentProps, SVGProps } from 'react';
import iconSpritePath from 'sprite/sprite.svg';
import { IconNames } from 'sprite/types';

interface IconSpriteProps extends Omit<SVGProps<SVGSVGElement>, 'ref'> {
  name: IconNames;
}

/**
 * Simple IconLookup component to return an icon based on the unique name.
 * @param name unique name of the icon you want to render
 * @param SVGProps optional styling by passing SVGProps
 * @returns Icon based on the unique name
 */
const RawIcon = ({ name, fontSize = '1em', ...rest }: IconSpriteProps) => {
  return (
    <svg width={fontSize} height={fontSize} {...rest}>
      <title>{name}</title>
      <use xlinkHref={`${iconSpritePath}#${name}`} />
    </svg>
  );
};

const MuiStyledIcon = styled(RawIcon, {
  shouldForwardProp: (prop) => prop !== 'rotateInArabic' && prop !== 'sx',
  skipVariantsResolver: true,
})<{ rotateInArabic?: boolean }>(({ rotateInArabic, theme }) => ({
  ...(rotateInArabic && {
    transform: `rotate(${theme.direction === 'rtl' ? '-180deg' : '0deg'})`,
  }),
}));

interface Props {
  fontSize?: number;
  rotateInArabic?: boolean;
  sx?: SxProps<Theme>;
}

const Icon = ({
  fontSize = 24,
  color,
  sx,
  rotateInArabic = false,
  ...rest
}: Omit<ComponentProps<typeof MuiStyledIcon>, 'fontSize'> & Props) => (
  <MuiStyledIcon
    rotateInArabic={rotateInArabic}
    sx={[
      {
        fontSize,
        minWidth: fontSize,
        color,
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
    {...rest}
  />
);

export default Icon;
